import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Responsive, useContainerWidth, type ResponsiveLayouts, type Layout } from 'react-grid-layout';
import { Shell } from './components/Shell';
import { useTranslation } from './i18n/useTranslation';
import TradingViewChart from './components/TradingViewChart';
import PnLPanel from './components/PnLPanel';
const ArsenalExplorer = lazy(() => import('./components/ArsenalExplorer').then(m => ({ default: m.ArsenalExplorer })));
import { RiskPanel } from './components/RiskPanel';
import { PortfolioPanel } from './components/PortfolioPanel';
const RdLoopDashboard = lazy(() => import('./components/RdLoopDashboard').then(m => ({ default: m.RdLoopDashboard })));
const JobMonitor = lazy(() => import('./components/JobMonitor').then(m => ({ default: m.JobMonitor })));
const ComboSidebar = lazy(() => import('./components/ComboSidebar').then(m => ({ default: m.ComboSidebar })));
import { KillSwitch } from './components/KillSwitch';
import { SignalFeed } from './components/SignalFeed';
import { AlphaEvalPanel } from './components/AlphaEvalPanel';
import { EquityCurvePanel } from './components/EquityCurvePanel';
import { TradeHistory } from './components/TradeHistory';
import { CusumGauge } from './components/CusumGauge';
import { ExternalFeedPanel } from './components/ExternalFeedPanel';
import { SwapLog } from './components/SwapLog';
import { AccountBalancePanel } from './components/AccountBalancePanel';
const OrderEntryPanel = lazy(() => import('./components/OrderEntryPanel').then(m => ({ default: m.OrderEntryPanel })));
const ComboGenomePanel = lazy(() => import('./components/ComboGenomePanel').then(m => ({ default: m.ComboGenomePanel })));
import { ToastContainer } from './components/ToastContainer';
import { DegradedDataBanner } from './components/DegradedDataBanner';
import { PanelState } from './components/PanelState';
const FeedbackWidget = lazy(() => import('./components/FeedbackWidget').then(m => ({ default: m.FeedbackWidget })));
const ParallelCoords = lazy(() => import('./components/ParallelCoords'));
const KdeDensity = lazy(() => import('./components/KdeDensity'));
const ComboDeepDive = lazy(() => import('./components/ComboDeepDive').then(m => ({ default: m.ComboDeepDive })));
const FloorGatesBadges = lazy(() => import('./components/FloorGatesBadges').then(m => ({ default: m.FloorGatesBadges })));
const RegimeOverlay = lazy(() => import('./components/RegimeOverlay').then(m => ({ default: m.RegimeOverlay })));
const ArsenalHeatmap = lazy(() => import('./components/ArsenalHeatmap'));
const EquityCurveViz = lazy(() => import('./components/EquityCurveViz').then(m => ({ default: m.EquityCurveViz })));
const RegimeTimeline = lazy(() => import('./components/RegimeTimeline').then(m => ({ default: m.RegimeTimeline })));
import { InstrumentSelector } from './components/InstrumentSelector';
const RightSidebar = lazy(() => import('./components/RightSidebar').then(m => ({ default: m.RightSidebar })));
import type { ScatterPoint } from './components/ScatterPlot3D';
import { PillarNav, type Pillar } from './components/PillarNav';
const RiskIntelPillar = lazy(() => import('./components/RiskIntelPillar').then(m => ({ default: m.RiskIntelPillar })));
const PortfolioIntelPillar = lazy(() => import('./components/PortfolioIntelPillar').then(m => ({ default: m.PortfolioIntelPillar })));
const WalletIntelPillar = lazy(() => import('./features/wallet/routes/WalletIntelRoute').then(m => ({ default: m.WalletIntelPillar })));
const MarketIntelPillar = lazy(() => import('./components/MarketIntelPillar').then(m => ({ default: m.MarketIntelPillar })));
const ActionBoardPanel = lazy(() => import('./components/ActionBoardPanel').then(m => ({ default: m.ActionBoardPanel })));
const AthenaAIPanel = lazy(() => import('./components/AthenaAIPanel').then(m => ({ default: m.AthenaAIPanel })));
const TradingDnaPanel = lazy(() => import('./components/TradingDnaPanel').then(m => ({ default: m.TradingDnaPanel })));
const BacktestingModePillar = lazy(() => import('./features/backtesting/BacktestingModePillar').then(m => ({ default: m.BacktestingModePillar })));

const ScatterPlot3D = lazy(() => import('./components/ScatterPlot3D'));
const VarConeChart = lazy(() => import('./components/VarConeChart'));
import { useTerminalStore } from './store/useTerminalStore';
import { useGatewayWS } from './hooks/useGatewayWS';
import { useBootstrap } from './hooks/useBootstrap';
import { useBarPolling } from './hooks/useBarPolling';
import { useEquityPolling } from './hooks/useEquityPolling';
import { usePanelState } from './hooks/usePanelState';
import { PANEL_SOURCE } from './lib/panelSources';
import { loadBarCacheEntry, saveBarCache, barLimit } from './hooks/useBarCache';
import { fetchBarsWithFallback } from './lib/bars';
import type { Timeframe } from './types/gateway';
import {
  DEFAULT_LAYOUTS,
  GRID_BREAKPOINTS,
  GRID_COLS,
  GRID_MARGIN,
  calcRowHeight,
  loadLayouts,
  saveLayouts,
  clearLayouts,
} from './layouts/tradingGrid';

type Tab = 'trading' | 'arsenal' | 'combo' | 'rdloop' | 'jobs' | 'action' | 'athena';
type ArsenalVizTab = '3d' | 'heatmap' | 'parallel' | 'density' | 'equity' | 'regime';

const TABS: readonly Tab[] = ['trading', 'arsenal', 'combo', 'rdloop', 'jobs', 'action', 'athena'] as const;
const TOP_LEVEL_PILLARS: readonly Pillar[] = ['dna', 'backtesting', 'portfolio', 'risk', 'wallet', 'market', 'asset'];
const PILLAR_SEARCH_PARAM = 'pillar';

const DATA_SOURCE_LABELS: Record<string, { labelKey: string; color: string }> = {
  live: { labelKey: 'data_source.live', color: 'bg-green-500' },
  degraded: { labelKey: 'data_source.degraded', color: 'bg-red-500' },
  mock: { labelKey: 'data_source.mock', color: 'bg-amber-500' },
  pending: { labelKey: 'data_source.pending', color: 'bg-muted' },
};

const ARSENAL_VIZ_TABS: { key: ArsenalVizTab; labelKey: string }[] = [
  { key: '3d', labelKey: 'arsenal.viz.3d' },
  { key: 'heatmap', labelKey: 'arsenal.viz.heatmap' },
  { key: 'parallel', labelKey: 'arsenal.viz.parallel' },
  { key: 'density', labelKey: 'arsenal.viz.density' },
  { key: 'equity', labelKey: 'arsenal.viz.equity' },
  { key: 'regime', labelKey: 'arsenal.viz.regime' },
];

function isPillar(value: string | null): value is Pillar {
  return value !== null && TOP_LEVEL_PILLARS.includes(value as Pillar);
}

function pillarFromLocation(): Pillar {
  if (typeof window === 'undefined') return 'asset';
  const url = new URL(window.location.href);
  const queryPillar = url.searchParams.get(PILLAR_SEARCH_PARAM);
  if (isPillar(queryPillar)) return queryPillar;

  const hashPillar = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
  if (isPillar(hashPillar)) return hashPillar;

  return 'dna';
}

function syncPillarUrl(pillar: Pillar) {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const hashPillar = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
  if (isPillar(hashPillar)) {
    url.hash = '';
  }
  if (pillar === 'dna') {
    url.searchParams.delete(PILLAR_SEARCH_PARAM);
  } else {
    url.searchParams.set(PILLAR_SEARCH_PARAM, pillar);
  }
  const next = `${url.pathname}${url.search}${url.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (next !== current) {
    window.history.replaceState(null, '', next);
  }
}

function AwaitingData({ label }: { label: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center h-full">
      <span className="text-[9px] font-mono text-muted animate-pulse">{t('common.awaiting')} {label}...</span>
    </div>
  );
}

function WalletIntelRouteLoadingFallback() {
  const { t } = useTranslation();
  return (
    <div
      aria-busy="true"
      aria-label={t('wallet_intel.loading.aria')}
      className="flex-1 min-h-0 flex flex-col p-4 pb-20 gap-4 bg-background"
      data-testid="wallet-intel-route-loading"
    >
      <div className="flex items-center justify-between shrink-0">
        <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
          {t('wallet_intel.loading.title')}
        </span>
        <span className="font-mono text-[9px] uppercase tracking-widest text-primary border border-primary/30 rounded-sm px-1.5 py-0.5">
          {t('wallet_intel.loading.badge')}
        </span>
      </div>
      <section className="hud-corner bg-card border border-border rounded p-3 min-h-32">
        <div className="flex items-center justify-between mb-2">
          <h2 className="panel-header">{t('wallet_intel.header')}</h2>
          <span className="text-[8px] font-mono uppercase text-muted-foreground border border-border rounded-sm px-1 py-0.5">
            {t('common.readonly')}
          </span>
        </div>
        <p className="text-[9px] font-mono text-muted-foreground leading-relaxed" role="status">
          {t('wallet_intel.loading.status')}
        </p>
      </section>
    </div>
  );
}

function TradingLayout() {
  const { t } = useTranslation();
  const symbol = useTerminalStore((s) => s.selectedSymbol);
  const bars = useTerminalStore((s) => s.bars);
  const signals = useTerminalStore((s) => s.signals);
  const combosMap = useTerminalStore((s) => s.combos);
  const alphaEvalsMap = useTerminalStore((s) => s.alphaEvals);
  const riskMetrics = useTerminalStore((s) => s.riskMetrics);
  const portfolioData = useTerminalStore((s) => s.portfolioData);
  const externalFeeds = useTerminalStore((s) => s.externalFeeds);
  const varCone = useTerminalStore((s) => s.varCone);
  const varConeState = usePanelState('varcone', PANEL_SOURCE.varcone);
  const weightHistory = useTerminalStore((s) => s.weightHistory);
  const accountBalance = useTerminalStore((s) => s.accountBalance);
  const orders = useTerminalStore((s) => s.orders);
  const combos = useMemo(() => Object.values(combosMap), [combosMap]);
  const firstAlphaEval = useMemo(() => {
    const entries = Object.values(alphaEvalsMap);
    return entries.length > 0 ? entries[0] : null;
  }, [alphaEvalsMap]);

  const [timeframe, setTimeframe] = useState<Timeframe>('dollar');
  const tfInitRef = useRef(true);

  useEffect(() => {
    if (tfInitRef.current) {
      tfInitRef.current = false;
      return;
    }
    useTerminalStore.getState().setSelectedTimeframe(timeframe);
    const sym = useTerminalStore.getState().selectedSymbol;
    const cachedEntry = loadBarCacheEntry(sym, timeframe);
    useTerminalStore.getState().setBars(cachedEntry?.bars ?? []);
    // Restore the fallback badge from cache synchronously — otherwise the previous
    // selection's 1m-proxy marker lingers over the new chart until the refetch resolves.
    useTerminalStore.getState().setBarsSource(timeframe, cachedEntry?.isFallback ?? false);
    fetchBarsWithFallback(sym, timeframe, barLimit(timeframe)).then((barsResult) => {
      useTerminalStore.getState().setBarsSource(barsResult.timeframeUsed, barsResult.isFallback);
      const mapped = barsResult.bars.map((b) => ({
        time: new Date(b.bar_close_time).getTime() / 1000,
        open: b.open, high: b.high, low: b.low, close: b.close, volume: b.volume,
      }));
      const dedup = new Map<number, (typeof mapped)[0]>();
      for (const bar of mapped) dedup.set(bar.time, bar);
      const sorted = [...dedup.values()].sort((a, b) => a.time - b.time);
      useTerminalStore.getState().setBars(sorted);
      saveBarCache(sym, timeframe, sorted, barsResult.isFallback);
    }).catch(() => {});
  }, [timeframe, symbol]);

  const fills = useMemo(() => {
    if (bars.length === 0) return [];
    const minTime = bars[0].time;
    const maxTime = bars[bars.length - 1].time;
    return signals
      .filter((s) => s.time >= minTime && s.time <= maxTime)
      .slice(0, 20)
      .map((s) => ({
        time: s.time,
        side: s.side,
        price: s.price,
      }));
  }, [signals, bars]);

  const handleTimeframeChange = useCallback((tf: Timeframe) => {
    setTimeframe(tf);
  }, []);

  const { width, containerRef, mounted } = useContainerWidth();
  const [layouts, setLayouts] = useState<ResponsiveLayouts>(() => loadLayouts() ?? DEFAULT_LAYOUTS);
  const [containerHeight, setContainerHeight] = useState(600);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setContainerHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [containerRef]);

  const rowHeight = calcRowHeight(containerHeight);

  const handleLayoutChange = useCallback((_current: Layout, allLayouts: ResponsiveLayouts) => {
    setLayouts(allLayouts);
    saveLayouts(allLayouts);
  }, []);

  const handleResetLayout = useCallback(() => {
    clearLayouts();
    setLayouts(DEFAULT_LAYOUTS);
  }, []);

  return (
    <div
      ref={containerRef}
      className="h-[1120px] min-h-[720px] w-full max-w-full overflow-hidden relative lg:h-full lg:min-h-0"
    >
      <button
        onClick={handleResetLayout}
        className="absolute top-1 right-1 z-20 px-2 py-0.5 text-[9px] font-mono
                   text-muted-foreground hover:text-foreground
                   bg-card/80 border border-border rounded-sm transition-colors"
        title={t('chart.reset_layout_title')}
      >
        {t('chart.reset_layout')}
      </button>

      {mounted && width > 0 && (
        <Responsive
          layouts={layouts}
          width={width}
          breakpoints={GRID_BREAKPOINTS}
          cols={GRID_COLS}
          rowHeight={rowHeight}
          margin={GRID_MARGIN}
          containerPadding={[6, 6]}
          onLayoutChange={handleLayoutChange}
          dragConfig={{ handle: '.drag-handle' }}
          resizeConfig={{ handles: ['se'] }}
        >
          <div key="chart" className="hud-corner bg-card rounded border border-border overflow-hidden flex flex-col animate-fade-up">
            <div className="drag-handle h-5 flex items-center px-2 cursor-grab active:cursor-grabbing border-b border-border-subtle shrink-0">
              <span className="panel-header">{t('chart.header')}</span>
            </div>
            <div className="flex-1 min-h-0">
              <TradingViewChart candles={bars} fills={fills} timeframe={timeframe} onTimeframeChange={handleTimeframeChange} />
            </div>
          </div>

          <div key="signals" className="hud-corner animate-fade-up" style={{ animationDelay: '0.03s' }}>
            <SignalFeed signals={signals} className="h-full" />
          </div>

          <div key="risk" className="hud-corner overflow-hidden animate-fade-up" style={{ animationDelay: '0.05s' }}>
            {riskMetrics ? (
              <RiskPanel risk={riskMetrics} className="h-full" />
            ) : (
              <AwaitingData label={t('labels.risk_metrics')} />
            )}
          </div>

          <div key="equity" className="hud-corner overflow-hidden animate-fade-up" style={{ animationDelay: '0.08s' }}>
            <EquityCurvePanel candles={bars} className="h-full" />
          </div>

          <div key="alphaeval" className="hud-corner overflow-hidden animate-fade-up" style={{ animationDelay: '0.1s' }}>
            {firstAlphaEval ? (
              <AlphaEvalPanel report={firstAlphaEval} className="h-full" />
            ) : (
              <AwaitingData label={t('labels.alpha_eval')} />
            )}
          </div>

          <div key="controls" className="overflow-hidden animate-fade-up" style={{ animationDelay: '0.12s' }}>
            <div className="h-full grid grid-rows-[1fr_auto] gap-1.5">
              <Suspense fallback={<AwaitingData label={t('labels.combos')} />}>
                <ComboSidebar
                  combos={combos}
                  onWeightChange={(combo, weight) => {
                    useTerminalStore.getState().updateComboWeight(combo, weight);
                    useTerminalStore.getState().addWeightChange(combo, weight);
                  }}
                  className="min-h-0"
                />
              </Suspense>
              <KillSwitch />
            </div>
          </div>

          <div key="trades" className="hud-corner overflow-hidden animate-fade-up" style={{ animationDelay: '0.15s' }}>
            <TradeHistory signals={signals} className="h-full" />
          </div>

          <div key="pnl" className="hud-corner overflow-hidden animate-fade-up" style={{ animationDelay: '0.18s' }}>
            <PnLPanel candles={bars} className="h-full" />
          </div>

          <div key="portfolio" className="hud-corner overflow-hidden animate-fade-up" style={{ animationDelay: '0.2s' }}>
            {portfolioData ? (
              <PortfolioPanel portfolio={portfolioData} className="h-full" />
            ) : (
              <AwaitingData label={t('labels.portfolio')} />
            )}
          </div>

          <div key="cusum" className="hud-corner overflow-hidden animate-fade-up" style={{ animationDelay: '0.22s' }}>
            <CusumGauge className="h-full" />
          </div>

          <div key="external" className="hud-corner overflow-hidden animate-fade-up" style={{ animationDelay: '0.24s' }}>
            <ExternalFeedPanel feeds={externalFeeds} className="h-full" />
          </div>

          <div key="varcone" className="hud-corner overflow-hidden animate-fade-up" style={{ animationDelay: '0.26s' }}>
            {varCone && varConeState !== 'degraded' ? (
              <Suspense fallback={<AwaitingData label={t('labels.var_cone')} />}>
                <VarConeChart data={varCone} className="h-full" />
              </Suspense>
            ) : (
              <PanelState state={varConeState} panelKey="varcone" />
            )}
          </div>

          <div key="swaplog" className="hud-corner overflow-hidden animate-fade-up" style={{ animationDelay: '0.28s' }}>
            <SwapLog history={weightHistory} className="h-full" />
          </div>

          <div key="account" className="hud-corner overflow-hidden animate-fade-up" style={{ animationDelay: '0.30s' }}>
            <AccountBalancePanel balance={accountBalance} className="h-full" />
          </div>

          <div key="orders" className="hud-corner overflow-hidden animate-fade-up" style={{ animationDelay: '0.32s' }}>
            <Suspense fallback={null}>
              <OrderEntryPanel orders={orders} className="h-full" />
            </Suspense>
          </div>
        </Responsive>
      )}
    </div>
  );
}

function ArsenalLayout() {
  const { t } = useTranslation();
  const genomesMap = useTerminalStore((s) => s.genomes);
  const alphaEvalsMap = useTerminalStore((s) => s.alphaEvals);
  const genomes = useMemo(() => Object.values(genomesMap), [genomesMap]);
  const firstAlphaEval = useMemo(() => {
    const entries = Object.values(alphaEvalsMap);
    return entries.length > 0 ? entries[0] : null;
  }, [alphaEvalsMap]);
  const [selectedCombo, setSelectedCombo] = useState<string | null>(null);
  const [vizTab, setVizTab] = useState<ArsenalVizTab>('3d');

  const selectedGenome = useMemo(
    () => genomes.find((g) => g.name === selectedCombo) ?? genomes[0] ?? null,
    [genomes, selectedCombo],
  );

  const scatterData = useMemo((): ScatterPoint[] =>
    genomes.map((g) => {
      const isMcs = g.scoring.tier === 'A' || g.scoring.tier === 'S';
      const isFloorFail = g.scoring.score < 0.3;
      const color = isFloorFail ? '#FF3366' : isMcs ? '#01E599' : '#F59E0B';

      // Shape from tactic
      const shape: ScatterPoint['shape'] =
        g.tactic === 'bracket' ? 'sphere'
        : g.tactic === 'mean_reversion' ? 'cube'
        : 'diamond';

      return {
        name: g.name,
        x: g.model_meta.ic_at_training,
        y: g.scoring.score,
        z: g.model_meta.inference_ms,
        color,
        shape,
        size: Math.max(0.04, Math.min(0.15, Math.abs(g.scoring.score) * 0.15)),
        group: `${g.model_meta.model_type}_${g.strategy_class}`,
        tooltip: {
          Sharpe: g.scoring.score.toFixed(2),
          IC: g.model_meta.ic_at_training.toFixed(4),
          Tier: g.scoring.tier,
          'Win Rate': (g.bracket.snr_threshold * 100).toFixed(1) + '%',
          Latency: g.model_meta.inference_ms.toFixed(2) + 'ms',
        },
      };
    }),
    [genomes]
  );

  if (genomes.length === 0) {
    return (
      <div className="h-full p-2 flex items-center justify-center">
        <AwaitingData label={t('labels.arsenal_genome')} />
      </div>
    );
  }

  return (
    <div className="h-full flex gap-2 p-2 overflow-hidden">
      {/* LEFT: Combo list */}
      <div className="w-64 shrink-0 flex flex-col gap-2">
        <ArsenalExplorer genomes={genomes} alphaEval={firstAlphaEval ?? undefined} className="flex-1 min-h-0" />
        <select
          value={selectedCombo ?? ''}
          onChange={(e) => setSelectedCombo(e.target.value || null)}
          className="text-[9px] font-mono bg-card-elevated text-foreground border border-border rounded-sm px-2 py-1"
          aria-label={t('arsenal.select_combo')}
        >
          {genomes.map((g) => (
            <option key={g.name} value={g.name}>{g.name}</option>
          ))}
        </select>
      </div>

      {/* CENTER: Tabbed visualization */}
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <nav className="flex items-center gap-1 shrink-0">
          {ARSENAL_VIZ_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setVizTab(tab.key)}
              className={`px-2 py-0.5 text-[8px] font-mono uppercase tracking-wider rounded-sm transition-colors ${
                vizTab === tab.key
                  ? 'bg-primary/15 text-primary border border-primary/30'
                  : 'text-muted-foreground hover:text-foreground hover:bg-card-elevated border border-transparent'
              }`}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </nav>
        <div className="flex-1 min-h-0">
          {vizTab === '3d' && (
            <Suspense fallback={
              <div className="h-full bg-card rounded border border-border flex items-center justify-center">
                <span className="text-[10px] font-mono text-muted">{t('scatter.loading')}</span>
              </div>
            }>
              <ScatterPlot3D data={scatterData} axisLabels={{ x: 'IC', y: 'Score', z: 'Latency' }} className="h-full" />
            </Suspense>
          )}
          {vizTab === 'heatmap' && <ArsenalHeatmap genomes={genomes} className="h-full" />}
          {vizTab === 'parallel' && <ParallelCoords genomes={genomes} className="h-full" />}
          {vizTab === 'density' && <KdeDensity genomes={genomes} className="h-full" />}
          {vizTab === 'equity' && <Suspense fallback={null}><EquityCurveViz genomes={genomes} selectedCombo={selectedCombo} className="h-full" /></Suspense>}
          {vizTab === 'regime' && <Suspense fallback={null}><RegimeTimeline className="h-full" /></Suspense>}
        </div>
      </div>

      {/* RIGHT: Combo detail */}
      <div className="w-72 shrink-0 flex flex-col gap-2 overflow-y-auto">
        <ComboDeepDive genome={selectedGenome} alphaEval={firstAlphaEval} className="shrink-0" />
        {firstAlphaEval ? (
          <>
            <div className="hud-corner bg-card rounded border border-border p-2">
              <div className="text-[8px] font-mono uppercase text-muted-foreground tracking-wider mb-1.5">{t('alphaeval.floor_gates')}</div>
              <FloorGatesBadges gates={firstAlphaEval.floor_gates} />
            </div>
            <div className="hud-corner bg-card rounded border border-border p-2">
              <div className="text-[8px] font-mono uppercase text-muted-foreground tracking-wider mb-1.5">{t('regime.performance')}</div>
              <RegimeOverlay perRegime={firstAlphaEval.per_regime} />
            </div>
          </>
        ) : (
          <AwaitingData label={t('labels.alpha_eval')} />
        )}
      </div>
    </div>
  );
}

function ComboLayout() {
  return (
    <div className="h-full">
      <Suspense fallback={null}>
        <ComboGenomePanel />
      </Suspense>
    </div>
  );
}

function RdLoopLayout() {
  const { t } = useTranslation();
  const rdLoopState = useTerminalStore((s) => s.rdLoopState);

  if (!rdLoopState) {
    return (
      <div className="h-full p-2 flex items-center justify-center">
        <AwaitingData label={t('labels.rdloop')} />
      </div>
    );
  }

  return (
    <div className="h-full p-2">
      <RdLoopDashboard state={rdLoopState} className="h-full" />
    </div>
  );
}

function JobsLayout() {
  const jobs = useTerminalStore((s) => s.jobs);
  return (
    <div className="h-full p-2">
      <JobMonitor jobs={jobs} className="h-full" />
    </div>
  );
}

function AssetIntelPillar() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>('trading');
  const symbol = useTerminalStore((s) => s.selectedSymbol);
  const dataSource = useTerminalStore((s) => s.dataSource);

  const sourceInfo = DATA_SOURCE_LABELS[dataSource] ?? DATA_SOURCE_LABELS.pending;

  return (
    <div
      className="flex-1 flex min-h-0 flex-col overflow-y-auto lg:flex-row lg:overflow-hidden"
      data-testid="asset-intel-pillar"
    >
      <InstrumentSelector selected={symbol} onSelect={(s) => useTerminalStore.getState().setSelectedSymbol(s)} />
      <div className="flex min-w-0 flex-col lg:flex-1 lg:min-h-0" data-testid="asset-intel-main">
        <nav className="flex items-center gap-1 min-h-8 px-2 lg:px-4 border-b border-border-subtle bg-card/30 shrink-0 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`shrink-0 px-3 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm transition-colors ${
                activeTab === tab
                  ? 'bg-primary/15 text-primary border border-primary/30'
                  : 'text-muted-foreground hover:text-foreground hover:bg-card-elevated'
              }`}
            >
              {t(`nav.${tab}` as Parameters<typeof t>[0])}
            </button>
          ))}
          <div className="ml-auto flex shrink-0 items-center gap-1.5 text-[9px] font-mono text-muted">
            <span className={`w-1.5 h-1.5 rounded-full ${sourceInfo.color}`} />
            {t(sourceInfo.labelKey)}
          </div>
        </nav>

        <div className="min-w-0 lg:flex-1 lg:min-h-0 lg:overflow-hidden" data-testid="asset-tab-content">
          {activeTab === 'trading' && <TradingLayout />}
          {activeTab === 'arsenal' && <Suspense fallback={null}><ArsenalLayout /></Suspense>}
          {activeTab === 'combo' && <ComboLayout />}
          {activeTab === 'rdloop' && <Suspense fallback={null}><RdLoopLayout /></Suspense>}
          {activeTab === 'jobs' && <Suspense fallback={null}><JobsLayout /></Suspense>}
          {activeTab === 'action' && (
            <div className="h-full p-2">
              <Suspense fallback={null}><ActionBoardPanel className="h-full" /></Suspense>
            </div>
          )}
          {activeTab === 'athena' && (
            <div className="h-full p-2">
              <Suspense fallback={null}><AthenaAIPanel className="h-full" /></Suspense>
            </div>
          )}
        </div>
      </div>
      <Suspense fallback={null}>
        <RightSidebar />
      </Suspense>
    </div>
  );
}

function App() {
  const { t } = useTranslation();
  const [activePillar, setActivePillar] = useState<Pillar>(() => pillarFromLocation());
  const liveDataEnabled = activePillar !== 'dna' && activePillar !== 'backtesting';
  useGatewayWS({ autoConnect: liveDataEnabled });
  useBootstrap(liveDataEnabled);
  useBarPolling(30_000, liveDataEnabled);
  useEquityPolling(60_000, liveDataEnabled);

  useEffect(() => {
    syncPillarUrl(activePillar);
  }, [activePillar]);

  useEffect(() => {
    const handlePopState = () => {
      setActivePillar(pillarFromLocation());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <Shell>
      <DegradedDataBanner />
      <PillarNav activePillar={activePillar} onPillarChange={setActivePillar} />
      <main className="flex-1 min-h-0 flex flex-col" id="main-content" aria-label={t('app.main_content')}>
        {activePillar === 'dna' && <Suspense fallback={null}><TradingDnaPanel /></Suspense>}
        {activePillar === 'backtesting' && <Suspense fallback={null}><BacktestingModePillar /></Suspense>}
        {activePillar === 'asset' && <AssetIntelPillar />}
        {activePillar !== 'dna' && activePillar !== 'backtesting' && activePillar !== 'asset' && (
          <div className="flex-1 min-h-0">
            {activePillar === 'portfolio' && <Suspense fallback={null}><PortfolioIntelPillar /></Suspense>}
            {activePillar === 'risk' && <Suspense fallback={null}><RiskIntelPillar /></Suspense>}
            {activePillar === 'wallet' && (
              <Suspense fallback={<WalletIntelRouteLoadingFallback />}>
                <WalletIntelPillar />
              </Suspense>
            )}
            {activePillar === 'market' && <Suspense fallback={null}><MarketIntelPillar /></Suspense>}
          </div>
        )}
      </main>
      <ToastContainer />
      <Suspense fallback={null}>
        <FeedbackWidget />
      </Suspense>
    </Shell>
  );
}

export default App;
